import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";

import { passwords } from "@/api/passwords";
import { ApiError } from "@/api/http";
import { useBranding } from "@/branding/BrandingLoader";
import { Button, Card, CardBody, CardHeader, FormError, Input, Label } from "@/components/ui";

export function ForgotPasswordPage() {
  const branding = useBranding();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = useMutation({
    mutationFn: () => passwords.forgot(email),
    onSuccess: () => setDone(true),
    onError: (e) => setError(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <h1 className="text-center text-base font-semibold">
            Reset your {branding?.name ?? "Piggy"} password
          </h1>
        </CardHeader>
        <CardBody>
          {done ? (
            <div className="space-y-3 text-sm">
              <p className="text-slate-600 dark:text-slate-400">
                If an account with that email exists, we sent a reset link. The link expires in
                an hour.
              </p>
              <Link to="/login" className="text-brand">
                ← Back to sign in
              </Link>
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                submit.mutate();
              }}
              className="space-y-3"
            >
              <p className="text-xs text-slate-500">
                Enter your email and we'll send a one-time reset link.
              </p>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <FormError>{error}</FormError>
              <Button type="submit" loading={submit.isPending} className="w-full">
                Send reset link
              </Button>
              <div className="mt-2 text-center text-xs text-slate-500">
                <Link to="/login" className="hover:text-brand">
                  ← Back to sign in
                </Link>
              </div>
            </form>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
